import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  useMediaQuery
} from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import useLocales from 'hooks/useLocales';
import WritableChip from 'components/shared/WritableChip/Index';
import Image from 'components/image/Image';
import ButtonComponent from 'components/Button/Index';
import { useAIOperationCosts } from 'hooks/useAIOperationCosts';
import { useAICreditAvailability } from 'hooks/useAICreditAvailability';
import useGenerateJobDescription from 'hooks/useGenerateJobDescription';

export const useTapToEdit = (): boolean =>
  useMediaQuery('(pointer: coarse)');

export const useInlineEditHint = (): string => {
  const { t } = useLocales();
  return useTapToEdit() ? t('Tap to edit') : t('Double-click to edit');
};

const quillModules = {
  toolbar: [
    [{ font: [] }],
    [{ size: ['small', false, 'large', 'huge'] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    [{ color: [] }, { background: [] }],
    ['clean']
  ],
  clipboard: {
    matchVisual: false
  }
};

const quillFormats = [
  'header',
  'font',
  'size',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'bullet',
  'indent',
  'align',
  'link',
  'color',
  'background'
];

type SaveHandler<T> = (value: T) => Promise<boolean> | boolean | void;

export const toNumericIds = (values: unknown): number[] => {
  const list = Array.isArray(values) ? values : values != null ? [values] : [];
  return list
    .map((item) => {
      if (typeof item === 'object' && item !== null && 'id' in item) {
        return Number((item as { id: number }).id);
      }
      return Number(item);
    })
    .filter((id) => Number.isFinite(id));
};

export const toStringList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? [trimmed] : [];
  }
  return [];
};

const sameIdSet = (
  left: Array<string | number>,
  right: Array<string | number>
): boolean => {
  const a = new Set(left.map(String));
  const b = new Set(right.map(String));
  return a.size === b.size && [...a].every((item) => b.has(item));
};

export const EditableShell: FC<{
  editing: boolean;
  disabled?: boolean;
  hint: string;
  onStart: () => void;
  className?: string;
  children: React.ReactNode;
}> = ({ editing, disabled, hint, onStart, className, children }) => {
  const tapToEdit = useTapToEdit();
  const startIfAllowed = () => {
    if (!editing && !disabled) {
      onStart();
    }
  };

  return (
    <div
      className={`company-job-detail__editable ${className ?? ''} ${
        editing ? 'is-editing' : ''
      } ${disabled ? 'is-disabled' : ''}`}
      title={editing || disabled ? undefined : hint}
      onClick={(event) => {
        if (!tapToEdit) {
          return;
        }
        const target = event.target as HTMLElement;
        if (target.closest('a, button, .MuiSelect-root')) {
          return;
        }
        startIfAllowed();
      }}
      onDoubleClick={() => {
        if (!tapToEdit) {
          startIfAllowed();
        }
      }}
    >
      {children}
    </div>
  );
};

export const InlineText: FC<{
  value: string;
  onSave: SaveHandler<string>;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  displayClassName?: string;
}> = ({
  value,
  onSave,
  disabled,
  placeholder,
  className,
  displayClassName
}) => {
  const hint = useInlineEditHint();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) {
      setDraft(value);
    }
  }, [value, editing]);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const finish = async (next: string) => {
    const trimmed = next.trim();
    setEditing(false);
    if (!trimmed || trimmed === value) {
      setDraft(value);
      return;
    }
    const ok = await onSave(trimmed);
    if (ok === false) {
      setDraft(value);
    }
  };

  if (editing) {
    return (
      <EditableShell
        editing
        disabled={disabled}
        hint={hint}
        onStart={() => undefined}
        className={className}
      >
        <TextField
          inputRef={inputRef}
          fullWidth
          value={draft}
          placeholder={placeholder}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => finish(draft)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              finish(draft);
            }
            if (event.key === 'Escape') {
              setDraft(value);
              setEditing(false);
            }
          }}
          size="small"
        />
      </EditableShell>
    );
  }

  return (
    <EditableShell
      editing={false}
      disabled={disabled}
      hint={hint}
      onStart={() => setEditing(true)}
      className={className}
    >
      <span className={displayClassName}>{value || placeholder}</span>
    </EditableShell>
  );
};

export const InlineSelect: FC<{
  value: string | number | '';
  items: Array<{ value: string | number; label: string }>;
  displayValue?: string;
  onSave: SaveHandler<string | number>;
  disabled?: boolean;
  emptyLabel?: string;
  className?: string;
}> = ({
  value,
  items,
  displayValue,
  onSave,
  disabled,
  emptyLabel,
  className
}) => {
  const hint = useInlineEditHint();
  const [editing, setEditing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!editing) {
      setMenuOpen(false);
      return;
    }
    const frame = window.requestAnimationFrame(() => setMenuOpen(true));
    return () => window.cancelAnimationFrame(frame);
  }, [editing]);

  const handleChange = async (event: SelectChangeEvent<string>) => {
    const next = event.target.value;
    setMenuOpen(false);
    setEditing(false);
    if (String(next) === String(value)) {
      return;
    }
    const numeric = Number(next);
    await onSave(Number.isNaN(numeric) ? next : numeric);
  };

  if (editing) {
    return (
      <EditableShell
        editing
        disabled={disabled}
        hint={hint}
        onStart={() => undefined}
        className={className}
      >
        <Select
          native={false}
          autoFocus
          open={menuOpen}
          fullWidth
          size="small"
          value={value === '' || value == null ? '' : String(value)}
          onClose={() => {
            setMenuOpen(false);
            setEditing(false);
          }}
          onChange={handleChange}
        >
          {items.map((item) => (
            <MenuItem key={String(item.value)} value={String(item.value)}>
              {item.label}
            </MenuItem>
          ))}
        </Select>
      </EditableShell>
    );
  }

  return (
    <EditableShell
      editing={false}
      disabled={disabled}
      hint={hint}
      onStart={() => setEditing(true)}
      className={className}
    >
      <span>{displayValue || emptyLabel || hint}</span>
    </EditableShell>
  );
};

export const InlineMultiSelect: FC<{
  values: Array<string | number>;
  items: Array<{ value: string | number; label: string }>;
  displayValue?: string;
  onSave: SaveHandler<Array<string | number>>;
  disabled?: boolean;
  emptyLabel?: string;
  onEditStart?: () => void;
}> = ({
  values,
  items,
  displayValue,
  onSave,
  disabled,
  emptyLabel,
  onEditStart
}) => {
  const hint = useInlineEditHint();
  const valuesKey = values.map(String).sort().join(',');
  const [editing, setEditing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [draft, setDraft] = useState<Array<string | number>>(values);

  useEffect(() => {
    if (!editing) {
      setDraft(values);
      setMenuOpen(false);
    }
    // valuesKey captures the selected ids; avoid resetting on new array identity.
  }, [valuesKey, editing]);

  useEffect(() => {
    if (!editing) {
      return;
    }
    const frame = window.requestAnimationFrame(() => setMenuOpen(true));
    return () => window.cancelAnimationFrame(frame);
  }, [editing]);

  const parseSelected = (next: string | string[]): Array<string | number> =>
    (typeof next === 'string' ? next.split(',').filter(Boolean) : next).map(
      (item) => {
        const numeric = Number(item);
        return Number.isNaN(numeric) ? item : numeric;
      }
    );

  const finish = async (next = draft) => {
    setMenuOpen(false);
    setEditing(false);
    if (sameIdSet(next, values)) {
      return;
    }
    await onSave(next);
  };

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    setDraft(parseSelected(event.target.value));
  };

  if (editing) {
    return (
      <EditableShell
        editing
        disabled={disabled}
        hint={hint}
        onStart={() => undefined}
      >
        <Select
          multiple
          autoFocus
          open={menuOpen}
          fullWidth
          size="small"
          value={draft.map(String)}
          onClose={() => {
            void finish();
          }}
          onChange={handleChange}
          renderValue={(selected) =>
            (selected as string[])
              .map(
                (id) =>
                  items.find((item) => String(item.value) === String(id))
                    ?.label || id
              )
              .join(', ')
          }
        >
          {items.map((item) => (
            <MenuItem key={String(item.value)} value={String(item.value)}>
              <Checkbox
                size="small"
                checked={draft.some(
                  (id) => String(id) === String(item.value)
                )}
              />
              {item.label}
            </MenuItem>
          ))}
        </Select>
      </EditableShell>
    );
  }

  return (
    <EditableShell
      editing={false}
      disabled={disabled}
      hint={hint}
      onStart={() => {
        onEditStart?.();
        setEditing(true);
      }}
    >
      <span>{displayValue || emptyLabel || hint}</span>
    </EditableShell>
  );
};

export const InlineRichText: FC<{
  value: string;
  onSave: SaveHandler<string>;
  disabled?: boolean;
  children: React.ReactNode;
  jobTitle?: string;
}> = ({ value, onSave, disabled, children, jobTitle }) => {
  const { t } = useLocales();
  const hint = useInlineEditHint();
  const { jobDescriptionCost } = useAIOperationCosts();
  const jobDescriptionCredits = useAICreditAvailability(jobDescriptionCost);
  const { generate, abort, isGenerating } = useGenerateJobDescription();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    if (!editing) {
      setDraft(value);
    }
  }, [value, editing]);

  useEffect(() => {
    if (!editing) {
      abort();
    }
  }, [abort, editing]);

  const handleSave = async () => {
    if (draft === value) {
      setEditing(false);
      return;
    }
    const ok = await onSave(draft);
    if (ok !== false) {
      setEditing(false);
    }
  };

  const handleGenerate = () => {
    if (isGenerating) {
      abort();
      return;
    }
    if (!jobTitle?.trim()) {
      return;
    }
    void generate(jobTitle, setDraft);
  };

  if (editing) {
    return (
      <div className="company-job-detail__editable is-editing company-job-detail__description-editor">
        <div
          className={`company-job-detail__description-editor-wrap${
            isGenerating ? ' company-job-detail__description-editor-wrap--generating' : ''
          }`}
        >
          <ReactQuill
            value={draft}
            onChange={setDraft}
            modules={quillModules}
            formats={quillFormats}
            theme="snow"
            readOnly={isGenerating}
            className={`quill-editor job-description-editor${
              isGenerating ? ' job-description-editor--generating' : ''
            }`}
          />
        </div>
        <div className="company-job-detail__edit-actions company-job-detail__edit-actions--description">
          {jobTitle !== undefined && (
            <Tooltip
              title={
                jobDescriptionCredits.isCreditLimited
                  ? jobDescriptionCredits.outOfCreditsTooltip
                  : t(`The operation will cost ${jobDescriptionCost} AI credits.`)
              }
              placement="top"
              arrow
            >
              <span className="company-job-detail__generate-wrap">
                <ButtonComponent
                  type="button"
                  t={t}
                  title={
                    isGenerating
                      ? t('Stop generation')
                      : t('Generate Job Description')
                  }
                  className="company-job-detail__generate-description"
                  onClick={handleGenerate}
                  disabled={
                    !jobTitle?.trim() || jobDescriptionCredits.isCreditLimited
                  }
                  isLoading={isGenerating}
                  keepEnabledOnLoading
                />
              </span>
            </Tooltip>
          )}
          <div className="company-job-detail__edit-actions-right">
            <Button size="small" onClick={() => setEditing(false)} disabled={isGenerating}>
              {t('Cancel')}
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleSave}
              disabled={isGenerating}
            >
              {t('Save')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <EditableShell
      editing={false}
      disabled={disabled}
      hint={hint}
      onStart={() => setEditing(true)}
    >
      {children}
    </EditableShell>
  );
};

export const InlineChips: FC<{
  values: unknown;
  onSave: SaveHandler<string[]>;
  disabled?: boolean;
  placeholder?: string;
  emptyLabel?: string;
}> = ({ values, onSave, disabled, placeholder, emptyLabel }) => {
  const { t } = useLocales();
  const hint = useInlineEditHint();
  const chipsKey = JSON.stringify(toStringList(values));
  const chips = useMemo(
    () => JSON.parse(chipsKey) as string[],
    [chipsKey]
  );
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string[]>(chips);
  const seedRef = useRef<string[]>(chips);
  const skipInitialChipSync = useRef(true);

  useEffect(() => {
    if (!editing) {
      setDraft(chips);
      skipInitialChipSync.current = true;
    }
  }, [chips, editing]);

  const startEditing = () => {
    seedRef.current = chips;
    setDraft(chips);
    skipInitialChipSync.current = true;
    setEditing(true);
  };

  const handleChipChange = (next: string[]) => {
    if (!Array.isArray(next)) {
      return;
    }
    if (skipInitialChipSync.current) {
      skipInitialChipSync.current = false;
      if (!next.length && seedRef.current.length) {
        return;
      }
    }
    setDraft(next);
  };

  const handleSave = async () => {
    const ok = await onSave(toStringList(draft));
    if (ok !== false) {
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <div className="company-job-detail__editable is-editing">
        <WritableChip
          defaultValues={seedRef.current}
          onSelectedValues={handleChipChange}
          placeholder={placeholder || t('Add and press Enter')}
        />
        <div className="company-job-detail__edit-actions">
          <Button size="small" onClick={() => setEditing(false)}>
            {t('Cancel')}
          </Button>
          <Button size="small" variant="contained" onClick={handleSave}>
            {t('Save')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <EditableShell
      editing={false}
      disabled={disabled}
      hint={hint}
      onStart={startEditing}
    >
      <span>{chips.length ? chips.join(', ') : emptyLabel || hint}</span>
    </EditableShell>
  );
};

export const SectionEditButton: FC<{
  onClick: () => void;
  label?: string;
}> = ({ onClick, label }) => {
  const { t } = useLocales();
  return (
    <button
      type="button"
      className="company-job-detail__section-edit"
      onClick={onClick}
      aria-label={label || t('Edit')}
    >
      <Image name="pencil.svg" width={14} height={14} />
      <span>{label || t('Edit')}</span>
    </button>
  );
};
